import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';
import ImageUpload from '../components/ImageUpload';

const Stores: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  // 新增/编辑在途锁（Modal 的 onOk 不感知 promise）+ 行内启停在途的行 ID
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/store', { params: { page: p, pageSize: 10 } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      // 经纬度清空时传 null，否则 undefined 不会更新到数据库
      if (values.latitude === undefined) values.latitude = null;
      if (values.longitude === undefined) values.longitude = null;
      if (editItem) {
        await request.put(`/store/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/store', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  // 行内启停：一次只允许一处在途，其余按钮置灰，避免连点把状态翻回原值
  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/store/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      onOk: async () => {
        await request.delete(`/store/${id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '门店名称', dataIndex: 'name' },
    { title: '联系电话', dataIndex: 'phone', render: (v: string) => v || '-' },
    { title: '地址', dataIndex: 'address', render: (v: string) => v || '-' },
    { title: '营业时间', dataIndex: 'businessHours', render: (v: string) => v || '-' },
    { title: '排序', dataIndex: 'sort', width: 80 },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? <Tag color="success">启用</Tag> : <Tag>禁用</Tag> },
    {
      title: '操作', width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => { setEditItem(record); form.setFieldsValue(record); setModalOpen(true); }}>编辑</Button>
          <Button
            size="small"
            loading={busyId === record.id}
            disabled={busyId !== null && busyId !== record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>门店管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditItem(null); form.resetFields(); setModalOpen(true); }}>
          新增门店
        </Button>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />
      <Modal title={editItem ? '编辑门店' : '新增门店'} open={modalOpen}
        onOk={handleSubmit} onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="门店名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="联系电话">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input />
          </Form.Item>
          <Form.Item label="经纬度（导航到店用，可从地图 App 复制）">
            <Space>
              <Form.Item name="latitude" noStyle>
                <InputNumber placeholder="纬度" style={{ width: 130 }} step={0.000001} precision={6} min={-90} max={90} />
              </Form.Item>
              <Form.Item name="longitude" noStyle>
                <InputNumber placeholder="经度" style={{ width: 130 }} step={0.000001} precision={6} min={-180} max={180} />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="image" label="门店图片">
            <ImageUpload tip="建议 1:1（如 600×600），小程序首页门店列表按正方形展示" />
          </Form.Item>
          <Form.Item name="businessHours" label="营业时间">
            <Input placeholder="如：周一至周五 8:00-17:30" />
          </Form.Item>
          <Form.Item name="description" label="门店介绍">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="sort" label="排序" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Stores;
